import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Wrench, Truck, CheckCircle, ArrowRight, Play, ChevronDown, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { categories, products, findProduct, productUrl, returns, suppliers, installTiers } from '../../content/products';
import { conditionsFor, glanceFor, docKind } from '../../lib/lockConditions';
import { toCents, fmt, exFromInc, fittedFromCents, tierIncCents } from '../../lib/shopPricing';
import { fetchAvailability, track } from '../../lib/shopApi';
import { useCart } from '../../context/CartContext';
import { ProductCard, ProductImg, Breadcrumbs, Html, STATUS_BADGE } from '../../components/shop/ShopBits';
import TradeQuoteForm from '../../components/shop/TradeQuoteForm';
import InstallPicker from '../../components/shop/InstallPicker';
import { imageIndexForVariant } from '../../lib/finish';
import A200Estimator from '../../components/shop/A200Estimator';
import usePageMeta from '../../hooks/usePageMeta';
import NotFound from '../NotFound';

// One line on availability: what it is and how long, nothing else.
function availabilityLine(status, supplierKey) {
  const s = suppliers[supplierKey] || { dispatchDays: [1, 2], transitDays: [1, 3] };
  if (status === 'van-stock') return 'In stock — delivered in 1–3 working days';
  if (status === 'backorder') return 'Back order — we confirm a date before charging';
  if (status === 'discontinued') return 'No longer available';
  return `Ordered in for you — ${s.dispatchDays[0] + s.transitDays[0]}–${s.dispatchDays[1] + s.transitDays[1]} working days`;
}

// Videos from our own installs. The lead clip sits in a real player with a poster frame
// and a play button on top of it — a bare <video> with the packshot as its poster read as
// "just another photo" (Hugh, 16 Sep). The other clips sit under it as thumbnails.
const VideoBlock = ({ clips, fallbackPoster }) => {
  const [idx, setIdx] = React.useState(0);
  const [playing, setPlaying] = React.useState(false);
  const ref = React.useRef(null);
  const clip = clips[idx];
  const pick = (i) => { setIdx(i); setPlaying(false); };
  const play = () => { setPlaying(true); const v = ref.current; if (v) { v.play().catch(() => {}); } };
  const mmss = (sec) => (sec ? `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}` : '');
  return (
    <figure className="mb-4" data-testid="product-videos">
      <div className="relative rounded-2xl overflow-hidden border border-gray-200 bg-black">
        <video
          key={clip.src}
          ref={ref}
          controls={playing}
          playsInline
          preload="metadata"
          poster={clip.poster || fallbackPoster}
          onPlay={() => setPlaying(true)}
          onEnded={() => setPlaying(false)}
          className="block mx-auto max-w-full max-h-[75vh]"
          data-testid="product-video"
        >
          <source src={clip.src} type="video/mp4" />
        </video>
        {!playing && (
          <button type="button" onClick={play} className="absolute inset-0 flex flex-col items-center justify-center text-white bg-black/25 hover:bg-black/35" aria-label={`Play video: ${clip.title}`} data-testid="product-video-play">
            <span className="flex items-center justify-center w-20 h-20 rounded-full bg-red-600 shadow-lg"><Play size={36} className="ml-1" fill="currentColor" /></span>
            <span className="mt-3 rounded-full bg-black/60 px-4 py-1.5 text-sm font-semibold">Watch the video{clip.seconds ? ` · ${mmss(clip.seconds)}` : ''}</span>
          </button>
        )}
      </div>
      <figcaption className="text-sm text-gray-700 mt-2"><span className="font-semibold text-gray-900">{clip.title}</span>{clip.caption ? ` — ${clip.caption}` : ''}</figcaption>
      {clips.length > 1 && (
        <ul className="grid grid-cols-4 gap-2 mt-3" data-testid="product-video-thumbs">
          {clips.map((c, i) => (
            <li key={c.src}>
              <button type="button" onClick={() => pick(i)} className={`group relative block w-full rounded-lg overflow-hidden border-2 ${i === idx ? 'border-red-600' : 'border-gray-200 hover:border-gray-400'}`} aria-label={`Show video: ${c.title}`} aria-pressed={i === idx}>
                <img src={c.poster || fallbackPoster} alt="" className="w-full aspect-square object-cover bg-gray-100" loading="lazy" />
                <span className="absolute inset-0 flex items-center justify-center"><span className="flex items-center justify-center w-8 h-8 rounded-full bg-black/60 text-white"><Play size={14} className="ml-0.5" fill="currentColor" /></span></span>
                <span className="block px-1 py-1 text-[11px] leading-tight text-gray-700 text-left">{c.title}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </figure>
  );
};

// The makers' documents as a row of short links ("User manual (PDF)"); the full title is the
// tooltip. Two documents of the same kind fall back to their full titles so they can be told apart.
const DocLinks = ({ items, testId }) => {
  if (!items.length) return null;
  const kinds = items.map((d) => docKind(d.label));
  const dup = new Set(kinds.filter((k, i) => kinds.indexOf(k) !== i));
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm" data-testid={testId}>
      {items.map((d, i) => (
        <li key={d.href}>
          <a href={d.href} target="_blank" rel="noreferrer" title={d.label} className="text-red-600 font-semibold inline-flex items-center gap-1">
            <FileText size={14} className="text-gray-400" />
            {dup.has(kinds[i]) ? d.label : kinds[i]}
            {/\.pdf(\?|$)/i.test(d.href) ? <span className="text-gray-400 font-normal">PDF</span> : null}
          </a>
        </li>
      ))}
    </ul>
  );
};

// Fit and warranty at a glance: four cells of two or three words, the makers' documents as
// short links, and the fine print folded away. Same facts as before, a tenth of the words
// on screen (Hugh, 17 Sep: "so much text it's insane").
const FitGlance = ({ product }) => {
  const cells = glanceFor(product);
  const rows = conditionsFor(product);
  const docs = (product.warrantyDocs || []).concat(product.docs || []);
  if (!cells.length && !rows.length && !docs.length) return null;
  return (
    <section className="mt-5" data-testid="product-conditions" aria-label="Fit and warranty">
      {cells.length > 0 && (
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-px rounded-xl overflow-hidden border border-gray-200 bg-gray-200" data-testid="fit-glance">
          {cells.map((c) => (
            <div key={c.label} className="bg-white px-3 py-3">
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{c.label}</dt>
              <dd className="font-bold text-gray-900 leading-tight mt-0.5">{c.value}</dd>
              {c.sub && <dd className="text-xs text-gray-600 mt-0.5">{c.sub}</dd>}
            </div>
          ))}
        </dl>
      )}
      {docs.length > 0 ? (
        <div className="mt-3"><DocLinks items={docs} testId="product-docs" /></div>
      ) : product.docsNote ? (
        <p className="text-sm text-gray-600 mt-3">{product.docsNote}</p>
      ) : null}
      {(rows.length > 0 || product.warrantyText) && (
        <details className="mt-3 group" data-testid="product-fine-print">
          <summary className="cursor-pointer text-sm font-semibold text-gray-900 inline-flex items-center gap-1 select-none">
            <ChevronDown size={16} className="text-gray-400 transition-transform group-open:rotate-180" /> The fine print
          </summary>
          <dl className="mt-3 space-y-2 text-sm border-l-2 border-gray-200 pl-4">
            {product.warrantyText && (
              <div>
                <dt className="font-semibold text-gray-900">Warranty</dt>
                <dd className="text-gray-700">
                  {product.warrantyText}{product.install.eligible ? ' Fitted by us: 12 months on our workmanship.' : ''}{' '}
                  <Link to="/shop/warranty" className="text-red-600 font-semibold">How claims work</Link>
                </dd>
              </div>
            )}
            {rows.map((r) => (
              <div key={r.label}>
                <dt className="font-semibold text-gray-900">{r.label}</dt>
                <dd className="text-gray-700">{r.text}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}
    </section>
  );
};

// A collapsed question; the answer only takes space when it is wanted.
const Faq = ({ q, a }) => (
  <details className="group border-t border-gray-200 py-3">
    <summary className="cursor-pointer font-semibold text-gray-900 flex items-start justify-between gap-3 select-none">
      <span>{q}</span>
      <ChevronDown size={18} className="flex-shrink-0 text-gray-400 mt-0.5 transition-transform group-open:rotate-180" />
    </summary>
    <p className="text-gray-700 mt-2 pr-8">{a}</p>
  </details>
);

const ProductPage = () => {
  const { category, slug } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const product = findProduct(slug);
  const valid = product && product.primaryCategory === category;
  usePageMeta();

  const { addItem, setInstall, cart } = useCart();
  const [variantId, setVariantId] = useState(params.get('variant') || (product ? product.variants[0].id : ''));
  // The chooser hands the door material over in the link (?material=…) so the install
  // question starts filled in. (params is read once.) No backset and no mortice choice on
  // this page: every mortice lock is sold as the full lockset and the size comes from the
  // door photos after payment (Hugh, 17 Sep 2026 — nothing between the customer and Pay).
  const fromChooser = (() => {
    const door = {};
    if (['timber', 'aluminium'].includes(params.get('material'))) door.doorMaterial = params.get('material');
    return { door };
  })();
  const [qty, setQty] = useState(1);
  const [withInstall, setWithInstall] = useState(false);
  const [installAnswers, setInstallAnswers] = useState(Object.assign({}, cart.install || {}, fromChooser.door));
  const [availability, setAvailability] = useState(null);
  const [added, setAdded] = useState(false);
  const [imageIdx, setImageIdx] = useState(0);

  useEffect(() => {
    if (!product) return;
    // Deep links carry ?variant=; read it once here (params is intentionally not a dependency).
    const initial = imageIndexForVariant(product.images, product.variants.find((v) => v.id === (params.get('variant') || '')) || product.variants[0]);
    setImageIdx(initial >= 0 ? initial : 0);
    track('view_item', { product: product.slug, price: product.priceIncGst });
    let cancelled = false;
    fetchAvailability()
      .then((data) => {
        if (!cancelled) setAvailability((data && data.variants) || {});
      })
      .catch(() => {
        if (!cancelled) setAvailability({});
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const variant = useMemo(() => (product ? product.variants.find((v) => v.id === variantId) || product.variants[0] : null), [product, variantId]);
  if (!valid) return <NotFound />;

  const cat = categories.find((c) => c.slug === product.primaryCategory);
  const saleStatus = product.saleStatus || 'sale';
  const badge = STATUS_BADGE[saleStatus];
  const buyable = saleStatus === 'sale' || saleStatus === 'install-only';
  const installLocked = saleStatus === 'install-only';
  const status = (availability && availability[variant.id] && availability[variant.id].status) || product.availabilityDefault;
  const fitted = fittedFromCents(product);
  const priceCents = toCents(product.priceIncGst);
  const canBuy = buyable && status !== 'discontinued';
  const related = product.related.map(findProduct).filter(Boolean);
  const accessories = product.accessories.map(findProduct).filter(Boolean);

  const chooseVariant = (id) => {
    setVariantId(id);
    const idx = imageIndexForVariant(product.images, product.variants.find((v) => v.id === id));
    if (idx >= 0) setImageIdx(idx);
    setParams(id === product.variants[0].id ? {} : { variant: id }, { replace: true });
  };

  const onAdd = (goToCart) => {
    if (!canBuy) return;
    addItem(variant.id, { qty, backsetMm: null, backsetMeasuredMm: null, noMortice: false, product });
    if ((withInstall || installLocked) && product.install.eligible) setInstall({ ...installAnswers, selected: true });
    setAdded(true);
    if (goToCart) navigate('/shop/cart');
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Breadcrumbs crumbs={[{ name: 'Home', path: '/' }, { name: 'Shop', path: '/shop' }, { name: cat.name, path: `/shop/${cat.slug}` }, { name: product.shortName }]} />
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            {(product.videos || product.video) && (
              <VideoBlock
                clips={product.videos || [{ src: product.video, poster: null, title: product.videoCaption || product.shortName, caption: '', seconds: 0 }]}
                fallbackPoster={product.images[0].src}
              />
            )}
            <div className={`aspect-square bg-white rounded-2xl border border-gray-200 flex items-center justify-center overflow-hidden${product.video ? ' hidden lg:flex' : ''}`}>
              <ProductImg
                key={(product.images[imageIdx] || product.images[0]).src}
                image={product.images[imageIdx] || product.images[0]}
                sizes="(min-width: 1024px) 600px, 100vw"
                className="max-h-full max-w-full object-contain"
                eager
              />
            </div>
            {product.images.length > 1 && (
              <div className="mt-3 flex flex-wrap gap-2" role="tablist" aria-label="Product photos">
                {product.images.map((im, i) => (
                  <button
                    key={im.src}
                    type="button"
                    role="tab"
                    aria-selected={i === imageIdx}
                    aria-label={im.alt}
                    onClick={() => setImageIdx(i)}
                    className={`w-16 h-16 rounded-lg border-2 bg-white overflow-hidden flex items-center justify-center ${i === imageIdx ? 'border-red-600' : 'border-gray-200 hover:border-gray-400'}`}
                  >
                    <img src={im.sm || im.src} alt="" width={64} height={64} className="max-h-full max-w-full object-contain" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            )}
            <FitGlance product={product} />
          </div>

          <div>
            <div className="flex items-center gap-3">
              <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">{product.brand}</p>
              {badge && <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${badge.cls}`}>{badge.label}</span>}
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{product.name}</h1>
            {product.ecosystems && product.ecosystems.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="ecosystem-chips">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Works with</span>
                {product.ecosystems.map((e) => (
                  <span key={e} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-900 text-white">{e}</span>
                ))}
              </div>
            )}
            {product.hub && <p className="text-sm text-gray-700 mb-4 rounded-lg bg-gray-50 border border-gray-200 p-3" data-testid="hub-note">{product.hub}</p>}

            {saleStatus === 'quote' && product.primaryCategory === 'door-automation' && <A200Estimator product={product} />}
            {saleStatus === 'quote' && product.primaryCategory !== 'door-automation' && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 mb-6">
                <p className="font-bold text-gray-900">Quoted per door, supply and install</p>
                <p className="text-sm text-gray-700 mt-1">Hardware from {fmt(priceCents)} incl. GST (indicative). The right unit depends on the door, the frame and the system it answers to, so we quote each door with its power, brackets and cabling — and we do not supply readers or controllers.</p>
              </div>
            )}
            {saleStatus === 'coming-soon' && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-6">
                <p className="font-bold text-gray-900">Coming soon</p>
                <p className="text-sm text-gray-700 mt-1">We are confirming supply through the NZ distributor. Leave your details and we will tell you the price and date as soon as it is settled.</p>
              </div>
            )}

            {/* Price block: fitted first (assurance pricing), supply-only beneath — one line each */}
            {buyable && (
            <div className="rounded-xl border border-gray-200 divide-y divide-gray-200 mb-6">
              {fitted && (
                <div className="px-4 py-3 flex items-center gap-3">
                  <Wrench className="text-red-600 flex-shrink-0" size={20} />
                  <div className="flex flex-wrap items-baseline gap-x-3">
                    <p className="text-2xl font-bold text-gray-900" data-testid="fitted-price">{fmt(fitted)} fitted</p>
                    <p className="text-sm text-gray-600">Auckland · lock + {installTiers[product.install.tier].label.toLowerCase()} {fmt(tierIncCents(product.install.tier))} · checked before we book</p>
                  </div>
                </div>
              )}
              {installLocked ? (
                <div className="px-4 py-3 text-sm text-gray-700">Supply &amp; install only (Auckland). Lock {fmt(priceCents)} incl. GST plus installation.</div>
              ) : (
                <div className="px-4 py-3 flex items-center gap-3">
                  <Truck className="text-gray-500 flex-shrink-0" size={20} />
                  <div className="flex flex-wrap items-baseline gap-x-3">
                    <p className="text-xl font-bold text-gray-900" data-testid="supply-price">{fmt(priceCents)} supply only</p>
                    <p className="text-sm text-gray-600">incl. GST · {fmt(exFromInc(priceCents))} ex · courier at checkout</p>
                  </div>
                </div>
              )}
            </div>
            )}

            {!buyable && product.primaryCategory !== 'door-automation' && (
              <div className="rounded-2xl border border-gray-200 p-5 mb-6">
                <h2 className="text-lg font-bold text-gray-900 mb-3">{saleStatus === 'quote' ? 'Request a quote for this door' : 'Register interest'}</h2>
                <TradeQuoteForm
                  pageSource={saleStatus === 'quote' ? 'shop-quote' : 'shop-interest'}
                  projectType={saleStatus === 'quote' ? 'Shop quote request' : 'Shop coming-soon interest'}
                  initialMessage={`${saleStatus === 'quote' ? 'Quote request' : 'Register interest'}: ${product.name}\n\nDoor / site: `}
                  submitLabel={saleStatus === 'quote' ? 'Request a quote' : 'Register interest'}
                  thanks={saleStatus === 'quote' ? 'Thanks — we will come back to you with a per-door quote.' : 'Thanks — we will email you when it is available.'}
                />
              </div>
            )}

            {/* Variant / finish */}
            {buyable && product.variants.length > 1 && (
              <div className="mb-5">
                <p className="text-sm font-semibold text-gray-900 mb-2">Finish</p>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map((v) => (
                    <button key={v.id} type="button" onClick={() => chooseVariant(v.id)} className={`px-3 py-2 rounded-full text-sm border ${variant.id === v.id ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'}`} aria-pressed={variant.id === v.id}>
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Mortice: always the full lockset, sized from the door photos after payment. */}
            {buyable && product.mortice && (
              <p className="text-sm text-gray-700 mb-5" data-testid="mortice-included">Mortice lock included, cut to size.</p>
            )}

            {/* Availability — one line */}
            {buyable && (
              <p className="text-sm text-gray-700 mb-5 flex items-center gap-2" data-testid="availability">
                <CheckCircle size={16} className={status === 'discontinued' || status === 'backorder' ? 'text-amber-500' : 'text-green-600'} />
                {availabilityLine(status, variant.supplier)}
              </p>
            )}

            {/* Install add-on */}
            {buyable && product.install.eligible && (
              <div className="mb-5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={withInstall || installLocked} disabled={installLocked} onChange={(e) => setWithInstall(e.target.checked)} className="mt-1 h-4 w-4" data-testid="install-toggle" />
                  <span>
                    <span className="font-semibold text-gray-900">{installLocked ? 'Supply & install in Auckland' : 'Add supply & install (Auckland)'}</span>
                    <span className="block text-sm text-gray-600">{installLocked ? 'This lock is only sold fitted.' : 'A few taps about the door give the fitted price.'}</span>
                  </span>
                </label>
                {(withInstall || installLocked) && (
                  <div className="mt-3">
                    <InstallPicker product={product} value={installAnswers} onChange={setInstallAnswers} count={qty} />
                  </div>
                )}
              </div>
            )}

            {/* Qty + add */}
            {buyable && (
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <label className="text-sm text-gray-700">
                Qty{' '}
                <select value={qty} onChange={(e) => setQty(Number(e.target.value))} className="ml-1 rounded-md border border-gray-300 px-2 py-2 bg-white">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <Button onClick={() => onAdd(false)} disabled={!canBuy} className="bg-red-600 hover:bg-red-700 text-white font-bold px-6" data-testid="add-to-cart">
                <ShoppingCart className="mr-2" size={18} /> Add to cart
              </Button>
              <Button onClick={() => onAdd(true)} disabled={!canBuy} variant="outline" className="border-gray-900 text-gray-900 font-semibold" data-testid="buy-now">
                Buy now <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
            )}
            {added && (
              <p className="text-sm text-green-700 mb-3" role="status">
                Added.{' '}
                <Link to="/shop/cart" className="font-semibold underline">
                  View cart
                </Link>
              </p>
            )}
            <p className="text-xs text-gray-600">
              {returns.changeOfMindDays}-day returns on unopened items · faulty items refunded in full ·{' '}
              <Link to="/shop/shipping-returns" className="underline">Details</Link>
            </p>
            <p className="text-sm mt-4">
              <Link to="/shop/find-my-lock" className="text-red-600 font-semibold inline-flex items-center gap-1" data-testid="product-find-my-lock">
                Not sure it fits? Find my lock <ArrowRight size={14} />
              </Link>
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="grid lg:grid-cols-3 gap-10 mt-14">
          <div className="lg:col-span-2">
            <Html html={product.description} />
            {product.install && product.install.eligible && product.install.tier === 'cut' && (
              <p className="text-sm text-gray-700 mt-2">
                <Link to="/shop/guides/fitting-a-digital-mortice-lock" className="text-red-600 font-semibold inline-flex items-center gap-1">How we fit a lock like this — 22 photos <ArrowRight size={14} /></Link>
              </p>
            )}

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-3">Specifications</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  {product.specs.map(([k, v]) => (
                    <tr key={k} className="border-t border-gray-200">
                      <th className="text-left py-2 pr-4 font-semibold text-gray-900 w-44 align-top">{k}</th>
                      <td className="py-2 text-gray-700">{v}</td>
                    </tr>
                  ))}
                  {product.unlock.length > 0 && (
                    <tr className="border-t border-gray-200">
                      <th className="text-left py-2 pr-4 font-semibold text-gray-900 align-top">Ways in</th>
                      <td className="py-2 text-gray-700">{product.unlock.join(' · ')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {product.faqs.length > 0 && (
              <>
                <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-3">Questions</h2>
                <div className="border-b border-gray-200" data-testid="product-faqs">
                  {product.faqs.map((f) => (
                    <Faq key={f.q} q={f.q} a={f.a} />
                  ))}
                </div>
              </>
            )}


          </div>

          <aside className="space-y-6">
            {accessories.length > 0 && (
              <div>
                <h3 className="font-bold text-gray-900 mb-3">Goes with it</h3>
                <ul className="space-y-2">
                  {accessories.map((a) => (
                    <li key={a.slug}>
                      <Link to={productUrl(a)} className="flex justify-between gap-3 text-sm border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-400">
                        <span className="text-gray-900">{a.shortName}</span>
                        <span className="font-semibold">{fmt(toCents(a.priceIncGst))}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Compare with</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProductPage;
